import { CodiceFiscale } from "codice-fiscale-js";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

const REL = ["nome", "cognome", "sesso", "data_nascita", "luogo_nascita", "provincia_nascita"];

export function computeCF(c) {
  try {
    if (!c.nome || !c.cognome || !c.sesso || !c.data_nascita || !c.luogo_nascita) return null;
    const [y, m, d] = c.data_nascita.split("-").map((x) => parseInt(x, 10));
    if (!y || !m || !d) return null;
    return CodiceFiscale.compute({
      name: c.nome, surname: c.cognome, gender: c.sesso,
      day: d, month: m, year: y,
      birthplace: c.luogo_nascita, birthplaceProvincia: (c.provincia_nascita || "").toUpperCase(),
    });
  } catch (e) {
    return null;
  }
}

export function CorsistaFields({ value, onChange, showAteco = false, showTipo = false, idPrefix = "corsista" }) {
  const c = value;
  const setField = (key, v) => {
    const next = { ...c, [key]: v };
    if (REL.includes(key) && !(c.codice_fiscale || "").trim()) {
      const cf = computeCF(next);
      if (cf) next.codice_fiscale = cf;
    }
    onChange(next);
  };
  const calcCF = () => {
    const cf = computeCF(c);
    if (cf) { onChange({ ...c, codice_fiscale: cf }); toast.success("Codice fiscale calcolato"); }
    else toast.error("Per il calcolo servono nome, cognome, sesso, data e comune di nascita (con provincia)");
  };
  return (
    <div className="grid grid-cols-2 gap-2 pr-5">
      <Input value={c.nome} onChange={(e) => setField("nome", e.target.value)} placeholder="Nome" className="h-8 text-sm" data-testid={`${idPrefix}-nome`} />
      <Input value={c.cognome} onChange={(e) => setField("cognome", e.target.value)} placeholder="Cognome" className="h-8 text-sm" data-testid={`${idPrefix}-cognome`} />
      <Select value={c.sesso || ""} onValueChange={(v) => setField("sesso", v)}>
        <SelectTrigger className="h-8 text-sm" data-testid={`${idPrefix}-sesso`}><SelectValue placeholder="Sesso" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="M">Maschio</SelectItem>
          <SelectItem value="F">Femmina</SelectItem>
        </SelectContent>
      </Select>
      <Input type="date" value={c.data_nascita} onChange={(e) => setField("data_nascita", e.target.value)} className="h-8 text-sm" title="Data di nascita" data-testid={`${idPrefix}-nascita`} />
      <Input value={c.luogo_nascita} onChange={(e) => setField("luogo_nascita", e.target.value)} placeholder="Comune di nascita" className="h-8 text-sm" data-testid={`${idPrefix}-luogo`} />
      <Input value={c.provincia_nascita} onChange={(e) => setField("provincia_nascita", e.target.value.toUpperCase().slice(0, 2))} placeholder="Prov. (es. AG)" className="h-8 text-sm" maxLength={2} data-testid={`${idPrefix}-prov`} />
      <div className="col-span-2 flex items-center gap-1.5">
        <Input value={c.codice_fiscale} onChange={(e) => onChange({ ...c, codice_fiscale: e.target.value.toUpperCase() })} placeholder="Codice fiscale (calcolo automatico)" className="h-8 text-sm flex-1" data-testid={`${idPrefix}-cf`} />
        <button type="button" onClick={calcCF} title="Calcola codice fiscale" className="h-8 px-2.5 rounded-md border border-dusty-300 text-dusty-800 hover:bg-dusty-50 inline-flex items-center gap-1 text-xs font-semibold shrink-0" data-testid={`${idPrefix}-calc-cf`}>
          <Calculator className="h-3.5 w-3.5" /> Calcola
        </button>
      </div>
      <Input value={c.email} onChange={(e) => onChange({ ...c, email: e.target.value })} placeholder="Email" className="h-8 text-sm" data-testid={`${idPrefix}-email`} />
      <Input value={c.telefono} onChange={(e) => onChange({ ...c, telefono: e.target.value })} placeholder="Telefono" className="h-8 text-sm" data-testid={`${idPrefix}-tel`} />
      <Input value={c.indirizzo} onChange={(e) => onChange({ ...c, indirizzo: e.target.value })} placeholder="Indirizzo di residenza" className="h-8 text-sm col-span-2" data-testid={`${idPrefix}-indirizzo`} />
      <Input value={c.citta} onChange={(e) => onChange({ ...c, citta: e.target.value })} placeholder="Città" className="h-8 text-sm" data-testid={`${idPrefix}-citta`} />
      <Input value={c.cap} onChange={(e) => onChange({ ...c, cap: e.target.value })} placeholder="CAP" className="h-8 text-sm" data-testid={`${idPrefix}-cap`} />
      <Input value={c.mansione} onChange={(e) => onChange({ ...c, mansione: e.target.value })} placeholder="Mansione" className="h-8 text-sm col-span-2" data-testid={`${idPrefix}-mansione`} />
      {showAteco && (
        <Input value={c.codice_ateco} onChange={(e) => onChange({ ...c, codice_ateco: e.target.value })} placeholder="Codice ATECO" className="h-8 text-sm col-span-2" data-testid={`${idPrefix}-ateco`} />
      )}
      {showTipo && (
        <Select value={c.tipo || ""} onValueChange={(v) => onChange({ ...c, tipo: v })}>
          <SelectTrigger className="h-8 text-sm col-span-2" data-testid={`${idPrefix}-tipo`}><SelectValue placeholder="Datore di Lavoro / Dipendente / Privato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="datore_lavoro">Datore di Lavoro</SelectItem>
            <SelectItem value="dipendente">Dipendente</SelectItem>
            <SelectItem value="privato">Privato</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
